import { Entity } from '@backstage/catalog-model';
import { GithubTeam } from '../../services/unifiedGithubService/unifiedGithubService.types';

/** Annotation written by `GithubOrgEntityProvider` on Group entities: `<org>/<team-slug>`. */
export const GITHUB_TEAM_SLUG_ANNOTATION = 'github.com/team-slug';

/** Team names/slugs that must never be offered as repository owners. */
export const INVALID_GITHUB_TEAM_OPTIONS = ['all', 'github-copilot'];

const equalsIgnoreCase = (a: string | undefined, b: string | undefined) =>
  a !== undefined && b !== undefined && a.toLowerCase() === b.toLowerCase();

const getDisplayName = (entity: Entity): string | undefined =>
  (entity.spec?.profile as { displayName?: string } | undefined)?.displayName;

/** Parses the `github.com/team-slug` annotation into its organization and slug. */
export const getGithubTeamIdentity = (entity: Entity): { org: string; slug: string } | undefined => {
  const value = entity.metadata.annotations?.[GITHUB_TEAM_SLUG_ANNOTATION];
  const [org, slug, ...rest] = value?.split('/') ?? [];
  if (!org || !slug || rest.length > 0) {
    return undefined;
  }
  return { org, slug };
};

/** Returns `true` when the team is a reserved option that must not be selectable. */
export const isInvalidGithubTeam = (team: GithubTeam): boolean =>
  INVALID_GITHUB_TEAM_OPTIONS.some(invalid => team.name === invalid || team.slug === invalid);

/**
 * Determines whether a catalog Group represents the given GitHub team.
 *
 * Groups ingested by `GithubOrgEntityProvider` are matched by organization and
 * slug (the `github.com/team-slug` annotation), so identically named teams in
 * different organizations stay distinct. Groups without that annotation keep
 * the legacy display-name match, limited to GHEC teams.
 */
export const entityMatchesGithubTeam = (entity: Entity, team: GithubTeam): boolean => {
  const identity = getGithubTeamIdentity(entity);
  if (identity) {
    if (!equalsIgnoreCase(identity.org, team.org)) {
      return false;
    }
    return team.slug ? equalsIgnoreCase(identity.slug, team.slug) : getDisplayName(entity) === team.name;
  }
  return team.origin !== 'emu' && getDisplayName(entity) === team.name;
};

/** Finds the GitHub team represented by a catalog Group, if any. */
export const findGithubTeamForEntity = (entity: Entity | undefined, teams: GithubTeam[]): GithubTeam | undefined =>
  entity ? teams.find(team => entityMatchesGithubTeam(entity, team)) : undefined;
